import { Offer, OfferApiPPP, OfferBase } from '../types';
import { ExternalAppName } from '../types/Apps';
import {
  getIntelligemsEnabledStatus,
  isIntelligemsAvailable,
} from './intelligemsUtils';

export type Placement = ExternalAppName;

export type IntelligemsOfferFilter = {
  mode: 'include' | 'exclude';
  offerIds: Array<number | string>;
};

export type OfferFiltersConfig = Partial<
  Record<Placement, IntelligemsOfferFilter>
>;

function isValidFilter(value: unknown): value is IntelligemsOfferFilter {
  const v = value as IntelligemsOfferFilter | undefined;
  return (
    !!v &&
    (v.mode === 'include' || v.mode === 'exclude') &&
    Array.isArray(v.offerIds)
  );
}

function getFiltersFromWindow(): OfferFiltersConfig | undefined {
  return typeof window !== 'undefined'
    ? (window.CANDYRACK_INTELLIGEMS_FILTERS as OfferFiltersConfig | undefined)
    : undefined;
}

function applyOfferFilter<T extends OfferBase>(
  offers: T[],
  filter?: IntelligemsOfferFilter,
): T[] {
  if (!isValidFilter(filter)) return offers;
  const idSet = new Set(filter.offerIds.map(String));
  return filter.mode === 'include'
    ? offers.filter((o) => idSet.has(String(o.id)))
    : offers.filter((o) => !idSet.has(String(o.id)));
}

/*
 * Popup placements: We can be sure that Intelligems window variable exists within the App
 */
export function applyIntelligemsFilterByPlacement<T extends OfferBase>(
  offers: T[],
  placement: Placement,
  isIntelligemsEnabled: boolean = false,
  filters: OfferFiltersConfig | undefined = getFiltersFromWindow(),
): T[] {
  const enabled = getIntelligemsEnabledStatus(isIntelligemsEnabled);
  if (!enabled) {
    return offers;
  }

  // Additional safety check: ensure Intelligems is actually available at runtime
  if (!isIntelligemsAvailable()) {
    return offers;
  }

  const rawFilter = filters?.[placement];
  if (rawFilter && !isValidFilter(rawFilter)) {
    // Misconfigured filter - return original offers
    return offers;
  }

  return applyOfferFilter(offers, rawFilter);
}

export function applyIntelligemsFilterForAddToCart(
  offers: Offer[],
  isIntelligemsEnabled: boolean,
): Offer[] {
  return applyIntelligemsFilterByPlacement<Offer>(
    offers,
    'add_to_cart',
    isIntelligemsEnabled,
  );
}

export function applyIntelligemsFilterForCheckoutButton(
  offers: Offer[],
  isIntelligemsEnabled: boolean,
): Offer[] {
  return applyIntelligemsFilterByPlacement<Offer>(
    offers,
    'checkout_button',
    isIntelligemsEnabled,
  );
}

export function applyIntelligemsFilterForProductPagePlacement(
  offers: OfferApiPPP[],
  isIntelligemsEnabled: boolean,
): OfferApiPPP[] {
  return applyIntelligemsFilterByPlacement<OfferApiPPP>(
    offers,
    'product_page_placement',
    isIntelligemsEnabled,
  );
}
